import { t } from "i18next";
import { Q3DSelect } from "../Parts/Q3DSelect";
import SearchBox from "./SearchBox";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FilterCombobox from "./parts/FilterCombobox";
import FilterTXTButton from "./parts/FilterTXTButton";
import { Typography, useTheme } from "@mui/material";

type Props = {
  userList: { value: string; label: string }[];
  jobsiteList: { value: string; label: string }[];
  isARAvailable: boolean;
};

export enum EStatus {
  ALL = "all",
  UPLOAD_COMPLETE = "1",
  PROCESSING_DONE = "2",
  COORDINATE_CHANGE_COMPLETE = "3",
  ACCURACY_VERIFY_COMP = "4",
  DASHBOARD_UPLOAD_COMPLETE = "5",
  AR_UPLOAD_COMPLETE = "6",
}

export const HeaderFilter = (props: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const search = location.search;
  const params = new URLSearchParams(search);
  const statusid = !params.get("status") ? "all" : params.get("status");
  const userid = !params.get("userid") ? "all" : params.get("userid");
  const jobsiteid = !params.get("jobsiteid") ? "all" : params.get("jobsiteid");
  const searchstr = !params.get("search") ? "" : params.get("search");
  const rtk = !params.get("rtk") ? 2 : params.get("rtk") === "true" ? 1 : 0;
  const remove = !params.get("remove") ? false : true;
  const theme = useTheme();

  const [status, setStatus] = useState<string>(statusid);
  const [userId, setUserId] = useState<string>(userid);
  const [jobsiteId, setJobsiteId] = useState<string>(jobsiteid);
  const [searchStr, setSearchStr] = useState<string>(searchstr);
  const [isRTK, setIsRTK] = useState<number>(rtk); // GPS:0 RTK:1 RTK/GPS:2
  const [isRemove, setIsRemove] = useState<boolean>(remove);

  useEffect(() => {
    console.log("navigate");
    const params = new URLSearchParams(search);
    status === EStatus.ALL
      ? params.delete("status")
      : params.set("status", status);
    params.set("userid", userId);
    jobsiteId === "all"
      ? params.delete("jobsiteid")
      : params.set("jobsiteid", jobsiteId);
    !searchStr ? params.delete("search") : params.set("search", searchStr);
    isRTK === 2 ? params.delete("rtk") : params.set("rtk", String(isRTK === 1));
    !isRemove ? params.delete("remove") : params.set("remove", "true");
    navigate(`?${params.toString()}`);
  }, [status, userId, jobsiteId, searchStr, isRTK, isRemove]);

  useEffect(() => {
    if (location.search.length > 0) return;
    const search = location.search;
    const params = new URLSearchParams(search);
    const statusid = !params.get("status") ? "all" : params.get("status");
    const userid = !params.get("userid") ? "all" : params.get("userid");
    const jobsiteid = !params.get("jobsiteid")
      ? "all"
      : params.get("jobsiteid");
    const searchstr = !params.get("search") ? "" : params.get("search");
    const rtk = !params.get("rtk") ? 2 : params.get("rtk") === "true" ? 1 : 0;
    const remove = !params.get("remove") ? false : true;

    setStatus(statusid);
    setUserId(userid);
    setJobsiteId(jobsiteid);
    setSearchStr(searchstr);
    setIsRTK(rtk);
    setIsRemove(remove);
  }, [location]);

  const FilterSelectItems = {
    status: [
      {
        value: EStatus.ALL,
        label: t("ALL"),
      },
      {
        value: EStatus.UPLOAD_COMPLETE,
        label: t("UPLOAD_COMPLETE"),
      },
      {
        value: EStatus.PROCESSING_DONE,
        label: t("PROCESSING_DONE"),
      },
      {
        value: EStatus.COORDINATE_CHANGE_COMPLETE,
        label: t("COORDINATE_CHANGE_COMPLETE"),
      },
      {
        value: EStatus.ACCURACY_VERIFY_COMP,
        label: t("ACCURACY_VERIFY_COMP"),
      },
      {
        value: EStatus.DASHBOARD_UPLOAD_COMPLETE,
        label: t("DASHBOARD_UPLOAD_COMPLETE"),
      },
      props.isARAvailable && {
        value: EStatus.AR_UPLOAD_COMPLETE,
        label: t("AR_UPLOAD_COMPLETE"),
      },
    ],
  };

  const strRTKLabel = (rtkValue: number) => {
    if (rtkValue === 0) {
      return "GPS";
    } else if (rtkValue === 1) {
      return "RTK";
    } else {
      return "GPS RTK";
    }
  };

  return (
    <>
      <Q3DSelect
        label={t("STATUS")}
        items={FilterSelectItems.status}
        value={status}
        setValue={setStatus}
        sx={{
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.base.border,
            borderWidth: "2px",
          },
        }}
      />
      <Q3DSelect
        label={t("PHOTOGRAPHER")}
        items={props.userList}
        value={userId}
        setValue={setUserId}
        sx={{
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.base.border,
            borderWidth: "2px",
          },
        }}
      />
      <FilterCombobox
        label={t("JOBSITE_NAME")}
        items={props.jobsiteList}
        value={jobsiteId}
        setValue={setJobsiteId}
      />
      <SearchBox
        value={searchStr}
        setValue={setSearchStr}
        inputSX={{
          ".MuiOutlinedInput-notchedOutline": {
            border: `2px solid ${theme.palette.base.border}`,
          },
          "input:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 30px transparent inset !important",
            boxShadow: "0 0 0 30px transparent inset !important",
            WebkitBackgroundClip: "text",
          },
        }}
      />
      <FilterTXTButton
        variant={isRTK !== 2 ? "contained" : "outlined"}
        colorTheme={isRTK === 0 ? "gps" : isRTK === 1 ? "rtk" : "secondary"}
        onClick={() => {
          setIsRTK((isRTK + 1) % 3);
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "0px !important",
        }}
      >
        {isRTK !== 1 && (
          <Typography
            component="div"
            sx={{
              fontSize: "12px",
              backgroundColor: "#4C6CB3",
              color: "white",
              width: "100%",
              height: isRTK === 0 ? "100%" : "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            gps
          </Typography>
        )}
        {isRTK !== 0 && (
          <Typography
            component="div"
            sx={{
              fontSize: "12px",
              backgroundColor: "#2E7D32",
              color: "white",
              width: "100%",
              height: isRTK === 1 ? "100%" : "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            rtk
          </Typography>
        )}
      </FilterTXTButton>
      <FilterTXTButton
        label={t("REMOVE_POINT_GROUP")}
        variant={isRemove ? "contained" : "outlined"}
        colorTheme={isRemove ? "primary" : "secondary"}
        onClick={() => {
          setIsRemove(!isRemove);
        }}
        sx={{
          maxWidth: "infinity",
          fontSize: "16px",
          whiteSpace: "break-spaces",
          overflow: "hidden",
          textOverflow: "ellipsis",
          border: isRemove ? `` : `2px solid ${theme.palette.base.border}`,
          "&:hover": {
            border: isRemove ? `` : `2px solid ${theme.palette.base.contrast}`,
          },
        }}
      />
    </>
  );
};
